import { Modifier, Schedule } from './schedule';
import { filterMap } from '../util/map';
import { add, subtract } from '../util/number';
import pill1Image from '../assets/images/pill/Pill1.png';
import pill2Image from '../assets/images/pill/Pill2.png';
import pill3Image from '../assets/images/pill/Pill3.png';
import pill4Image from '../assets/images/pill/Pill4.png';

export type StatusValue<T> = {
    value: T;
    optimal: T;
    min: T;
    max: T;
    key: string;
    color: string;
}

export type Status = {
    hp: StatusValue<number>;
    happiness: StatusValue<number>;
    //sleepiness: StatusValue<number>;
    sickness: StatusValue<number>;
    //isAsleep: StatusValue<boolean>;
    bloodPressure: StatusValue<number>;
};

export enum EffectType {
    Neutral,
    Ailment,
    Pill,
}

type StatusEffectApplication = (
    self: StatusEffect,
    time: number,
    statusEffects: Map<string, StatusEffect>,
    status: Status,
    schedule: Schedule
) => Status

type StatusEffectPredicate = (
    self: StatusEffect,
    time: number,
    statusEffects: Map<string, StatusEffect>,
    status: Status,
    schedule: Schedule
) => boolean

type StatusPredicate = (
    self: StatusEffect,
    time: number,
    statusEffects: Map<string, StatusEffect>,
    status: Status,
    schedule: Schedule
) => boolean


// TODO add to ailments
export type EffectPresentation = {
    icon: string;
    nameKey?: string;
}

export type BaseEffect = {
    key: string;
    duration: number;
    startTime: number;
    apply?: StatusEffectApplication;
    onTick: StatusEffectApplication;
    cleanup?: StatusEffectApplication;
}

export type Pill = BaseEffect & EffectPresentation & {
    type: EffectType.Pill;
}

export type Ailment = BaseEffect & {
    type: EffectType.Ailment;
}

export type Neutral = BaseEffect & {
    type: EffectType.Neutral;
}

export type StatusEffect = Pill | Ailment | Neutral;

/*
const applyIfNotSleeping: StatusEffectApplication = (self, time, statusEffects, status, schedule) => {
    if (status.isAsleep.value) {
        return status;
    }

    statusEffects.set(self.key, { ...self, startTime: time });
    return status;
};
*/

const apply: StatusEffectApplication = (self, time, statusEffects, status, schedule) => {
    statusEffects.set(self.key, { ...self, startTime: time });
    return status;
};

const logEffect: StatusEffectApplication = (self, time, statusEffects, status, schedule) => {
    console.log({
        self,
        time,
        statusEffects,
        status,
        schedule,
    });
    return status;
};

const changeStatusValue = <K extends keyof Status>(key: K, change: (prev: Status[K]['value']) => Status[K]['value'], pred?: StatusEffectPredicate): StatusEffectApplication =>
    (self, time, statusEffects, status, schedule) => {
        if (!!pred && !pred(self, time, statusEffects, status, schedule)) {
            return status;
        }
        return {
            ...status,
            [key]: {
                ...status[key],
                value: change(status[key].value),
            },
        };
    };

const hasEffect = (key: string): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) =>
        statusEffects.has(key);

const hasModifier = (modifier: Modifier, offset = 0): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) =>
        schedule[time + offset].modifiers.includes(modifier);

const not = (pred: StatusEffectPredicate): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) => !pred(self, time, statusEffects, status, schedule);

const and = (...preds: StatusEffectPredicate[]): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) => preds.every((p) => p(self, time, statusEffects, status, schedule));

const or = (...preds: StatusEffectPredicate[]): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) => preds.some((p) => p(self, time, statusEffects, status, schedule));

const restDurationCompare = (compare: (v: number) => boolean): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) => compare(self.duration - (time - self.startTime));

const removeEffect = (key: string, pred?: StatusEffectPredicate): StatusEffectApplication => (self, time, statusEffects, status, schedule) => {
    if (!!pred && !pred(self, time, statusEffects, status, schedule)) {
        return status;
    }
    if (statusEffects.has(key)) {
        statusEffects.delete(key);
    }

    return status;
};


const removeEffectOfType = (type: EffectType, pred?: StatusEffectPredicate): StatusEffectApplication => (self, time, statusEffects, status, schedule) => {
    if (!!pred && !pred(self, time, statusEffects, status, schedule)) {
        return status;
    }
    filterMap(statusEffects, (s: StatusEffect) => s.type !== type);

    return status;
};


const addEffect = (effect: StatusEffect, pred?: StatusEffectPredicate): StatusEffectApplication => (self, time, statusEffects, status, schedule) => {
    if (!!pred && !pred(self, time, statusEffects, status, schedule)) {
        return status;
    }
    statusEffects.set(effect.key, effect);
    return status;
};

const changeOwnDuration = (change: (prev: number) => number, pred?: StatusEffectPredicate): StatusEffectApplication => (self, time, statusEffects, status, schedule) => {
    if (!!pred && !pred(self, time, statusEffects, status, schedule)) {
        return status;
    }

    self.duration = change(self.duration);
    return status;
};

const statusValueCompare = <K extends keyof Status>(key: K, compare: (prev: Status[K]['value'], status: Status[K]) => boolean): StatusEffectPredicate =>
    (self, time, statusEffects, status, schedule) => compare(status[key].value, status[key]);

const chain = (...fns: (StatusEffectApplication)[]): StatusEffectApplication => (self, time, statusEffects, status, schedule) => {
    return fns.reduce((acc, fn) => fn(self, time, statusEffects, acc, schedule), status);
};

const set = <T, K extends T>(value: K) => (prev: K) => value;

export const melancholy: Ailment = {
    key: 'ailment.melancholy',
    type: EffectType.Ailment,
    duration: 5,
    startTime: -1,
    onTick: changeStatusValue('happiness', subtract(2)),
};

/*export const insomnia: Ailment = {
    key: 'ailment.insomnia',
    type: EffectType.Ailment,
    duration: 10,
    startTime: -1,
    onTick: changeStatusValue(
        'sleepiness',
        add(1),
        and(
            hasModifier(Modifier.isNight),
            statusValueCompare('isAsleep', equals(false))
        )
    ),
};*/

export const stomachache: Ailment = {
    key: 'ailment.stomachache',
    type: EffectType.Ailment,
    duration: 10,
    startTime: -1,
    onTick: chain(
        changeStatusValue('hp', subtract(1)),
        changeStatusValue('happiness', subtract(1))
    ),
};

export const headache: Ailment = {
    key: 'ailment.headache',
    type: EffectType.Ailment,
    duration: 10,
    startTime: -1,
    onTick: chain(
        changeStatusValue('hp', subtract(1)),
        changeStatusValue('happiness', subtract(1))
    ),
};

export const sickness: Ailment = {
    key: 'ailment.sickness',
    type: EffectType.Ailment,
    duration: 10,
    startTime: -1,
    onTick: chain(
        changeStatusValue('sickness', add(1)),
        changeStatusValue('happiness', subtract(1)),
    ),
};

export const hypertension: Ailment = {
    key: 'ailment.hypertension',
    type: EffectType.Ailment,
    duration: 10,
    startTime: -1,
    onTick: chain(
        changeStatusValue('bloodPressure', add(1)),
    ),
};

export const hypotension: Ailment = {
    key: 'ailment.hypotension',
    type: EffectType.Ailment,
    duration: 10,
    startTime: -1,
    onTick: chain(
        changeStatusValue('bloodPressure', subtract(1)),
    ),
};

/*export const sleep: Neutral = {
    key: 'neutral.sleep',
    type: EffectType.Neutral,
    duration: 2,
    startTime: -1,
    apply: changeStatusValue('isAsleep', set(true)),
    onTick: chain(
        changeStatusValue('sleepiness', subtract(1)),
        changeOwnDuration(set(0), statusValueCompare('sleepiness', lessThanOrEqual(0))),
    ),
    cleanup: changeStatusValue('isAsleep', set(false)),
};*/

export const living: Neutral = {
    key: 'neutral.living',
    type: EffectType.Neutral,
    duration: 10,
    startTime: -1,
    onTick: chain(
        // fall asleep
        /*addEffect(
            sleep,
            statusValueCompare('sleepiness', (v, spec) => v >= spec.max)
        ),*/
        // kotzi kotzi
        removeEffectOfType(EffectType.Pill, statusValueCompare('sickness', (v, spec) => v >= spec.max)),
        changeStatusValue('sickness', set(0), statusValueCompare('sickness', (v, spec) => v >= spec.max)),
        changeStatusValue('hp', subtract(3), statusValueCompare('sickness', (v, spec) => v >= spec.max)),
        // blood pressure
        changeStatusValue('hp', subtract(2), statusValueCompare('bloodPressure', (v, spec) => Math.abs(v - spec.optimal) > 2)),
    ),
};

export const tumbumrum: Pill = {
    key: 'pill.tumbumrum',
    icon: pill1Image,
    type: EffectType.Pill,
    duration: 3,
    startTime: -1,
    apply: chain(
        changeStatusValue('sickness', set(0), hasModifier(Modifier.isMealtime)),
        apply
    ),
    onTick: chain(
        removeEffect(sickness.key),
    ),
};

export const chillma: Pill = {
    key: 'pill.chillma',
    icon: pill2Image,
    type: EffectType.Pill,
    duration: 6,
    startTime: -1,
    apply: chain(
        addEffect(headache),
        changeStatusValue('bloodPressure', set(5), statusValueCompare('bloodPressure', (v, spec) => Math.abs(v - spec.optimal) > 2)),
        apply
    ),
    onTick: chain(
        changeStatusValue('bloodPressure', subtract(1)),
    ),
};

/*export const slupninox: Pill = {
    key: 'pill.slupninox',
    icon: pill2Image,
    type: EffectType.Pill,
    duration: 2,
    startTime: -1,
    //apply: applyIfNotSleeping,
    onTick: chain(
        changeStatusValue('hp', subtract(1), hasEffect(tumbumrum.key)),
        removeEffect(insomnia.key)
    ),
};*/

export const hapxi: Pill = {
    key: 'pill.hapxi',
    icon: pill3Image,
    type: EffectType.Pill,
    duration: 3,
    startTime: -1,
    apply: chain(
        changeStatusValue('hp', subtract(3), hasEffect('pill.hapxi')),
        changeStatusValue('happiness', add(4)),
        apply
    ),
    onTick: chain(
        removeEffect(melancholy.key),
    ),
};

export const antiau: Pill = {
    key: 'pill.antiau',
    icon: pill4Image,
    type: EffectType.Pill,
    duration: 3,
    startTime: -1,
    apply: chain(
        changeStatusValue('sickness', add(1)),
        apply
    ),
    onTick: chain(
        removeEffect(headache.key),
    ),
};
